import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, SearchableSelect, useToast, usePincodeLookup, type SearchableSelectOption } from '@kss/ui';
import {
  ArrowLeft, Calendar as CalendarIcon, ChevronDown, ChevronUp, Mail, MessageCircle,
  Plus, Printer, ScanLine, Settings, Share2, Trash2, Save, X,
} from 'lucide-react';
import { apiClient } from '@kss/api';
import { postStockMovements } from '../../utils/inventory';
import { postLinesSequential, createHeaderAndGetId } from '../../utils/postLines';
import { printSalesOrder, type SalesOrderPrintInput, type TenantHeader, type SalesOrderBank } from '../../print/templates';
import { printQrUrl, getPrintBrandingInlined, getTenantHeaderInlined } from '../../print/branding';
import { getTenantHeader } from '../../print/branding';
import { postPurchasePayment, paymentStatusFor } from '../../utils/payments';

/**
 * Full-page transaction-entry shell.
 *
 * Mirrors the BillBook demo layout pixel-for-pixel:
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ ← Sales Order                              Credit | Cash  │
 *   │                                                           │
 *   │   Business Info :                                         │
 *   │   ┌─ Doc No ───┐  ┌─ Date ────┐                           │
 *   │   │ KS/2025/1  │  │ 17/04/25  │                           │
 *   │   └────────────┘  └───────────┘                           │
 *   │                                                           │
 *   │   Vendor / Customer Info                                  │
 *   │   ┌─Vendor*─┐ ┌─Phone─┐ ┌─Place─┐ ┌─Valid Till─┐ ┌─Date─┐ │
 *   │                                                           │
 *   │   ▼ Line Item Stock                                       │
 *   │   ┌─ Barcode ─────────────────────────────────────────┐   │
 *   │   ┌──────────────────────────────────────────────────┐   │
 *   │   │ # │ Item │ Variant │ Qty │ Unit │ Price │ Gross │   │
 *   │   │ 1 │ … …  │  …      │ 1   │  …   │  0    │ 0     │   │
 *   │   │ + Add New                                        │   │
 *   │   └──────────────────────────────────────────────────┘   │
 *   │                                                           │
 *   │   Summary  / Save                                         │
 *   └───────────────────────────────────────────────────────────┘
 *
 * One shell, parameterised per resource (SalesOrder, SalesInvoice,
 * SalesReturn, CreditNote, DebitNote, DeliveryChallan, PurchaseOrder,
 * PurchaseInvoice, GoodsReceiptNote). The `presetForKind` table at
 * the bottom decides:
 *   - which counter-party we ask for (customer vs supplier)
 *   - which optional fields render in the info row
 *   - which extra fields render in the toggle (With/Without Item,
 *     Cash/Credit, etc.)
 *   - what API endpoint the Save button POSTs to.
 */

export interface TransactionKindPreset {
  /** Heading shown next to the back arrow. */
  title: string;
  /** Document-number field label. */
  docNumberLabel: string;
  /** Counter-party label — "Vendor*" for purchase flows, "Customer*" for sales. */
  partyLabel: string;
  /** Counter-party endpoint for the search options. */
  partyResource: 'customer' | 'supplier';
  /** Live API base path the form POSTs to. */
  basePath: string;
  /** "/panel/api/<group>/<resource>" — where the back arrow returns. */
  listPath: string;
  /** Top-right toggle: 'credit-cash' or 'with-without-item' or undefined. */
  modeToggle?: 'credit-cash' | 'with-without-item';
  /** Whether to render the Valid-Till + Delivery-Date pair (PO only). */
  showDeliveryDates?: boolean;
  /** Whether to render the Org-Bill-Number + Org-Bill-Date pair (Debit Note). */
  showOriginalBill?: boolean;

  /* --- Real schema-column mapping (so data lands in the actual columns,
     not only in an opaque `description` blob). Probed from live swagger. --- */
  /** Header column that stores the document number (orderNumber/invoiceNumber/…). */
  numberField?: string;
  /** Header column that stores the document date (orderDate/issueDate/…). */
  dateField?: string;
  /** Party FK column on the header (customerId | supplierId). */
  partyIdField?: string;
  /** Party display-name column, when the schema has one (customerName | supplierName). */
  partyNameField?: string;
  /** String workflow-status column + its default value on create. */
  statusField?: string;
  statusValue?: string;
  /** Money columns — defaults: subTotal/taxAmount/grandTotal. DebitNote uses `amount`;
      SalesReturn uses `refundAmount` as its grand total; DeliveryChallan has none. */
  subTotalField?: string;
  taxField?: string;
  grandTotalField?: string;
  /** Line table base path + its FK column. Omit when the entity has no line table
      (DeliveryChallan) — then lines fall back to the description JSON only. */
  lineBasePath?: string;
  lineFkField?: string;

  /** Show a Warehouse picker (purchase docs that move stock into a location). */
  showWarehouse?: boolean;
  /** When set, posting this doc writes InventoryMovement rows in that direction
      ('In' = receipt → stock up). Purchase Invoice = 'In'. */
  stockDirection?: 'In' | 'Out';
  /** Show an "Amount paid" field that records a PurchasePayment (Purchase Invoice). */
  showPayment?: boolean;
  /** Show the retail-only line columns (Variant, Add-Ons). Off for B2B docs
      (Sales Order, PO, Credit/Debit Note) so the line table stays clean. */
  lineExtras?: boolean;
  /** Show Billing / Shipping address fields and persist them to the header.
      Only enable for entities whose live schema actually has those columns —
      probed: SalesOrder has `billingAddress`/`shippingAddress`; the other
      sales/purchase docs do NOT, so writing them there would be silently
      dropped (or 500). */
  addressFields?: boolean;
  /** Optional "import from a source document" picker (e.g. a Sales Return
      pulling lines from the original Sales Invoice). When set, the shell shows
      a picker that, on select, prefills the party + the line items from that
      document so the operator doesn't re-key them. */
  referenceResource?: {
    basePath: string;        // source header endpoint, e.g. '/sales/SalesInvoice'
    lineBasePath: string;    // source line endpoint, e.g. '/sales/SalesInvoiceLine'
    lineFkField: string;     // FK on source lines → source header id
    numberField: string;     // source header's human number (invoiceNumber)
    dateField: string;       // source header's date
    label: string;           // picker label, e.g. 'Returning against invoice'
    /** Column on THIS doc's header that stores the source document id
        (e.g. SalesReturn.salesInvoiceId). Persisted on save so the return is
        linked to its origin, not just captured as free text. */
    headerFkField?: string;
  };
}

export interface TransactionLine {
  id: string;
  productId?: string | number | null;
  itemName: string;
  variant?: string;
  addOns?: string;
  qty: number;
  unit?: string;
  price: number;
  taxRate?: number;
}

interface TransactionEntryShellProps {
  kind: string;
}

/* ============================================================ */
/*  Component                                                   */
/* ============================================================ */

/** Per-kind code prefix for the auto-generated document number. */
const KIND_PREFIX: Record<string, string> = {
  SalesOrder: 'SO', SalesInvoice: 'SI', SalesReturn: 'SR',
  CreditNote: 'CN', DebitNote: 'DN', DeliveryChallan: 'DC',
  PurchaseOrder: 'PO', PurchaseInvoice: 'PI', GoodsReceiptNote: 'GRN',
};

/** Unique document number, e.g. "SO-LK9F2A" — avoids 409 duplicate_code. */
function newDocNumber(kind: string): string {
  const prefix = KIND_PREFIX[kind] ?? 'DOC';
  const suffix = Date.now().toString(36).toUpperCase().slice(-6);
  return `${prefix}-${suffix}`;
}

export default function TransactionEntryShell({ kind }: TransactionEntryShellProps) {
  const preset = PRESETS[kind];
  const navigate = useNavigate();
  const toast = useToast();
  const { id: editId } = useParams<{ id?: string }>();
  const isEdit = !!editId && editId !== 'new';

  /* ----- Top-right toggle ------------------------------------ */
  const [mode, setMode] = useState<string>(preset.modeToggle === 'credit-cash' ? 'Credit' : 'WithItem');

  /* ----- Business Info --------------------------------------- */
  // A fresh doc gets a UNIQUE code per kind. The old default of '1'
  // collided with the existing code='1' row → 409 duplicate_code (esp.
  // SalesOrder). User can still overwrite the field before saving.
  const [docNumber, setDocNumber] = useState<string>(isEdit ? '' : newDocNumber(kind));
  // The loaded row's numeric id — sent as the PUT key on edit (codes are
  // often empty, and the PUT endpoints resolve by `id`).
  const [editRowId, setEditRowId] = useState<number | string | null>(null);
  // Print / share extras.
  const [tenantHdr, setTenantHdr] = useState<TenantHeader>({ name: 'My Company' });
  const [buyerPoNumber, setBuyerPoNumber] = useState('');
  const [transporter, setTransporter] = useState('');
  const [actionsOpen, setActionsOpen] = useState(false);
  // The full saved header row (real persisted totals: discount, shipping,
  // tax, round-off) + the default bank account — both bound into the print.
  const [savedRow, setSavedRow] = useState<Record<string, unknown> | null>(null);
  const [bankInfo, setBankInfo] = useState<SalesOrderBank | null>(null);
  useEffect(() => {
    void apiClient.get('/accounting/BankAccount').then((r) => {
      const d = r.data;
      const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
      const b = rows.find((x) => x.isDefault || x.isPrimary) ?? rows[0];
      if (b) setBankInfo({
        accountName: String(b.accountName ?? b.name ?? ''),
        bankName: String(b.bankName ?? b.bank ?? ''),
        accountNo: String(b.accountNumber ?? b.accountNo ?? ''),
        ifsc: String(b.ifscCode ?? b.ifsc ?? ''),
        branch: String(b.branchName ?? b.branch ?? ''),
      });
    }).catch(() => { /* no bank configured — footer omits it */ });
  }, []);
  useEffect(() => {
    // No backend tenant endpoint (`/tenants/Tenant/me` → 404, gap G86) — read
    // the print header from the admin-set branding (Settings → Print Formats).
    setTenantHdr(getTenantHeader());
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const [docDate, setDocDate] = useState<string>(today);

  /* ----- Party Info ------------------------------------------ */
  const [partyId, setPartyId] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [placeOfSupply, setPlaceOfSupply] = useState<string>('');
  const [validTill, setValidTill] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [origBillNo, setOrigBillNo] = useState<string>('');
  const [origBillDate, setOrigBillDate] = useState<string>('');
  // Billing / shipping address (real columns on SalesOrder, PurchaseInvoice, …).
  const [billingAddress, setBillingAddress] = useState<string>('');
  const [shippingAddress, setShippingAddress] = useState<string>('');
  const [shipSameAsBill, setShipSameAsBill] = useState<boolean>(true);

  /* Pincode auto-fill for Place of Supply when user types a 6-digit
     PIN — reuses the same hook the customer/supplier forms use. */
  const [pin, setPin] = useState<string>('');
  const pinLookup = usePincodeLookup(pin);
  useEffect(() => {
    if (pinLookup.status === 'ok' && pinLookup.state) {
      setPlaceOfSupply((prev) => prev || pinLookup.state || '');
    }
  }, [pinLookup.status, pinLookup.state]);

  /* ----- Line items ------------------------------------------ */
  const [linesOpen, setLinesOpen] = useState<boolean>(true);
  const [barcode, setBarcode] = useState<string>('');
  const [lines, setLines] = useState<TransactionLine[]>(() => [newLine()]);

  function newLine(): TransactionLine {
    return { id: makeId(), itemName: '', qty: 1, price: 0 };
  }
  function addLine() { setLines((ls) => [...ls, newLine()]); }
  function removeLine(id: string) { setLines((ls) => ls.filter((l) => l.id !== id)); }
  function updateLine(id: string, patch: Partial<TransactionLine>) {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  /* ----- Party options --------------------------------------- */
  const [partyOptions, setPartyOptions] = useState<{ value: string; label: string }[]>([]);
  // Raw party rows kept so selecting a customer/vendor can auto-fill its
  // phone + pincode (the options array only carries value/label).
  const [partyRows, setPartyRows] = useState<Array<Record<string, unknown>>>([]);
  /* ----- Warehouse (purchase stock-in) ----------------------- */
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouseOptions, setWarehouseOptions] = useState<SearchableSelectOption[]>([]);
  /* ----- Payment (purchase invoice) -------------------------- */
  const [amountPaid, setAmountPaid] = useState(0);
  const [payMethod, setPayMethod] = useState('Cash');
  const [partyReload, setPartyReload] = useState(0);
  const partyUrl = preset.partyResource === 'customer' ? '/crm/Customer' : '/purchase/Supplier';
  useEffect(() => {
    let cancelled = false;
    apiClient.get(partyUrl).then((res) => {
      if (cancelled) return;
      const d = res.data;
      const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
      setPartyRows(rows);
      setPartyOptions(
        rows.slice(0, 500).map((r) => ({
          // §35.3: FK column stores numeric id; prefer it over code.
          value: String(r.id ?? r[`${preset.partyResource}Id`] ?? r.code ?? ''),
          label: String(r.name ?? r.code ?? `#${r.id}`),
        })).filter((o) => o.value),
      );
    }).catch(() => { /* keep empty options */ });
    return () => { cancelled = true; };
  }, [partyUrl, partyReload]);

  /* ----- Reference document (e.g. Sales Return ← Sales Invoice) ----
     When the preset declares a referenceResource, load its headers as
     options; on select, prefill the party + import the source line items so
     the operator reduces qty instead of re-keying every product (#5). */
  const refCfg = preset.referenceResource;
  const [refId, setRefId] = useState('');
  const [refOptions, setRefOptions] = useState<SearchableSelectOption[]>([]);
  const [refRows, setRefRows] = useState<Array<Record<string, unknown>>>([]);
  useEffect(() => {
    if (!refCfg) return;
    let cancelled = false;
    apiClient.get(refCfg.basePath).then((res) => {
      if (cancelled) return;
      const d = res.data;
      const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
      setRefRows(rows);
      setRefOptions(
        rows
          // Drop only the auto-seeded SAMPLE rows. Do NOT require a non-empty
          // invoiceNumber — some live invoices were saved with a blank number
          // (the payment-wipe bug), and they're still valid to return against.
          // Keep any row that has a number, a customer, or a non-zero total.
          .filter((r) => !String(r.code ?? '').startsWith('SAMPLE-')
            && (String(r[refCfg.numberField] ?? '').trim() || String(r.customerName ?? '').trim() || Number(r.grandTotal ?? 0) > 0))
          .slice(0, 500)
          .map((r) => ({
            value: String(r.id ?? ''),
            label: String(r[refCfg.numberField] ?? r.code ?? `#${r.id}`),
            description: [String(r.customerName ?? ''), r.grandTotal != null ? `₹${r.grandTotal}` : '']
              .filter(Boolean).join(' · '),
          }))
          .filter((o) => o.value),
      );
    }).catch(() => { /* keep empty */ });
    return () => { cancelled = true; };
  }, [refCfg]);

  async function importFromReference(v: string) {
    setRefId(v);
    if (!refCfg || !v) return;
    const head = refRows.find((r) => String(r.id) === v);
    if (!head) return;
    // Prefill party + original-bill identifiers from the source header.
    if (head.customerId != null) pickParty(String(head.customerId));
    setOrigBillNo(String(head[refCfg.numberField] ?? head.code ?? ''));
    const dt = head[refCfg.dateField]; if (dt) setOrigBillDate(String(dt).slice(0, 10));
    // Import the source line items (same shape the edit-hydrate path produces).
    try {
      const lr = await apiClient.get(refCfg.lineBasePath);
      const ld = lr.data;
      const lrows = (Array.isArray(ld) ? ld : (ld?.items ?? ld?.data ?? [])) as Array<Record<string, unknown>>;
      const mine = lrows
        .filter((x) => String(x[refCfg.lineFkField]) === String(head.id))
        .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
      if (mine.length) {
        setLines(mine.map((l) => ({
          id: makeId(),
          productId: (l.productId ?? null) as string | number | null,
          itemName: String(l.productName ?? l.name ?? ''),
          qty: Number(l.quantity ?? 1) || 1,
          unit: l.unitOfMeasure ? String(l.unitOfMeasure) : undefined,
          price: Number(l.unitPrice ?? 0),
          taxRate: l.taxRate != null ? Number(l.taxRate) : undefined,
        })));
        toast.success('Items imported', `${mine.length} line(s) from ${String(head[refCfg.numberField] ?? '')} — adjust return quantities as needed`);
      } else {
        toast.info?.('No line items', 'That document has no itemised lines to import.');
      }
    } catch { /* leave lines as-is */ }
  }

  /* ----- Product options (for line-item picker) -------------- */
  const [productRows, setProductRows] = useState<Array<Record<string, unknown>>>([]);
  const [productReload, setProductReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    apiClient.get('/master-data/Product').then((res) => {
      if (cancelled) return;
      const d = res.data;
      const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
      setProductRows(rows);
    }).catch(() => setProductRows([]));
    return () => { cancelled = true; };
  }, [productReload]);

  // #1/#2 — variants (per-line dropdown), bundles ("Add bundle").
  const [variants, setVariants] = useState<Array<Record<string, unknown>>>([]);
  const [bundleItems, setBundleItems] = useState<Array<Record<string, unknown>>>([]);
  const [bundles, setBundles] = useState<Array<Record<string, unknown>>>([]);
  useEffect(() => {
    const pull = (p: string) =>
      apiClient.get(p).then((r) => {
        const d = r.data;
        const l = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
        return Array.isArray(l) ? (l as Array<Record<string, unknown>>) : [];
      }).catch(() => [] as Array<Record<string, unknown>>);
    void Promise.all([
      pull('/master-data/ProductVariant'),
      pull('/master-data/ProductBundleItem'),
      pull('/master-data/ProductBundle'),
      pull('/master-data/Warehouse'),
    ]).then(([v, bi, b, wh]) => {
      setVariants(v); setBundleItems(bi); setBundles(b);
      const opts = wh.map((w) => ({ value: String(w.id ?? w.code ?? ''), label: String(w.name ?? w.code ?? '') })).filter((o) => o.value);
      setWarehouseOptions(opts);
      if (opts[0]) setWarehouseId((cur) => cur || opts[0]!.value); // default to first warehouse
    });
  }, []);
  const variantsFor = (pid: string | number | null | undefined) =>
    pid == null ? [] : variants.filter((v) => String(v.productId) === String(pid));
  const variantLabel = (v: Record<string, unknown>) =>
    [v.size, v.color].filter(Boolean).join(' / ') || String(v.name ?? v.sku ?? 'Variant');
  const bundleOptions: SearchableSelectOption[] = useMemo(
    () => bundles
      // Only offer bundles that actually have component items — hides
      // orphaned/empty bundles that would yield "Empty bundle".
      .filter((b) => bundleItems.some((bi) => String(bi.productBundleId) === String(b.id ?? b.code)))
      .map((b) => ({ value: String(b.id ?? b.code ?? ''), label: String(b.name ?? b.code ?? '') }))
      .filter((o) => o.value),
    [bundles, bundleItems],
  );

  /** #2 — append a bundle's component products as line items. */
  function addBundle(bundleId: string) {
    const bundle = bundles.find((b) => String(b.id ?? b.code) === String(bundleId));
    if (!bundle) return;
    const items = bundleItems.filter((bi) => String(bi.productBundleId) === String(bundle.id ?? bundle.code));
    if (!items.length) { toast.error('Empty bundle', 'This bundle has no products yet.'); return; }
    const byId: Record<string, Record<string, unknown>> = {};
    for (const p of productRows) byId[String(p.id ?? p.productId)] = p;
    const newLines: TransactionLine[] = items.map((bi) => {
      const prod = byId[String(bi.productId)] ?? {};
      return {
        id: makeId(),
        productId: (bi.productId ?? null) as string | number | null,
        itemName: String(prod.name ?? bi.productId ?? ''),
        qty: Number(bi.quantity ?? 1) || 1,
        unit: String(prod.unitOfMeasure ?? prod.unit ?? ''),
        price: Number(bi.unitPrice ?? prod.price ?? prod.mrp ?? 0),
        taxRate: prod.taxRate != null ? Number(prod.taxRate) : undefined,
      };
    });
    setLines((ls) => {
      const real = ls.filter((l) => l.productId != null || l.itemName.trim());
      return [...real, ...newLines];
    });
    toast.success('Bundle added', `${items.length} item(s) from ${String(bundle.name ?? '')}`);
  }

  /* ----- Edit: hydrate the existing document --------------------
     The shell persists its rich payload (party, dates, line items,
     totals) as JSON in the `description` column. On edit we fetch the
     row by code/id, restore the header fields, and parse the lines back
     so the operator sees what was saved (and can update it). */
  useEffect(() => {
    if (!isEdit || !editId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.get(preset.basePath);
        const d = res.data;
        const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
        const row = rows.find(
          (x) => String(x.code ?? '') === String(editId) || String(x.id ?? '') === String(editId),
        );
        if (!row || cancelled) return;
        setEditRowId((row.id ?? null) as number | string | null);
        setSavedRow(row);
        setDocNumber(String(row.code ?? editId));
        let meta: Record<string, unknown> = {};
        try { meta = JSON.parse(String(row.description ?? '{}')) as Record<string, unknown>; } catch { /* not JSON */ }
        // Fields with no schema column come from the description envelope.
        if (typeof meta.mode === 'string') setMode(meta.mode);
        if (typeof meta.phone === 'string') setPhone(meta.phone);
        if (typeof meta.placeOfSupply === 'string') setPlaceOfSupply(meta.placeOfSupply);
        if (typeof meta.pin === 'string') setPin(meta.pin);
        if (typeof meta.validTill === 'string') setValidTill(meta.validTill);
        if (typeof meta.deliveryDate === 'string') setDeliveryDate(meta.deliveryDate);
        if (typeof meta.origBillNo === 'string') setOrigBillNo(meta.origBillNo);
        if (typeof meta.origBillDate === 'string') setOrigBillDate(meta.origBillDate);
        if (typeof meta.buyerPoNumber === 'string') setBuyerPoNumber(meta.buyerPoNumber);
        if (typeof meta.transporter === 'string') setTransporter(meta.transporter);
        // Real columns take precedence over the description blob.
        const realDate = preset.dateField ? row[preset.dateField] : undefined;
        if (realDate) setDocDate(String(realDate).slice(0, 10));
        else if (typeof meta.docDate === 'string') setDocDate(meta.docDate);
        const realParty = preset.partyIdField ? row[preset.partyIdField] : undefined;
        if (realParty != null) setPartyId(String(realParty));
        else if (meta.partyId != null) setPartyId(String(meta.partyId));

        // Billing / shipping — real columns win; fall back to the envelope.
        if (preset.addressFields) {
          const bill = String(row.billingAddress ?? meta.billingAddress ?? '');
          const ship = String(row.shippingAddress ?? meta.shippingAddress ?? '');
          if (bill) setBillingAddress(bill);
          if (ship) setShippingAddress(ship);
          // If they differ, the operator had unticked "same as billing".
          setShipSameAsBill(!ship || ship === bill);
        }

        // Lines — prefer the REAL line table; fall back to the description
        // envelope for records saved before the line-table fix shipped.
        let usedRealLines = false;
        if (preset.lineBasePath && preset.lineFkField && row.id != null) {
          try {
            const lr = await apiClient.get(preset.lineBasePath);
            const ld = lr.data;
            const lrows = (Array.isArray(ld) ? ld : (ld?.items ?? ld?.data ?? [])) as Array<Record<string, unknown>>;
            const fk = preset.lineFkField;
            const mine = lrows
              .filter((x) => String(x[fk]) === String(row.id))
              .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
            if (mine.length && !cancelled) {
              setLines(mine.map((l) => ({
                id: makeId(),
                productId: (l.productId ?? null) as string | number | null,
                itemName: String(l.productName ?? l.name ?? ''),
                qty: Number(l.quantity ?? 1) || 1,
                unit: l.unitOfMeasure ? String(l.unitOfMeasure) : undefined,
                price: Number(l.unitPrice ?? 0),
                taxRate: l.taxRate != null ? Number(l.taxRate) : undefined,
              })));
              usedRealLines = true;
            }
          } catch { /* fall back to description lines */ }
        }
        if (!usedRealLines && !cancelled) {
          const savedLines = Array.isArray(meta.lines) ? (meta.lines as Array<Record<string, unknown>>) : [];
          if (savedLines.length) {
            setLines(savedLines.map((l) => ({
              id: makeId(),
              productId: (l.productId ?? null) as string | number | null,
              itemName: String(l.name ?? ''),
              variant: l.variant ? String(l.variant) : undefined,
              addOns: l.addOns ? String(l.addOns) : undefined,
              qty: Number(l.quantity ?? 1) || 1,
              unit: l.unit ? String(l.unit) : undefined,
              price: Number(l.price ?? 0),
              taxRate: l.taxRate != null ? Number(l.taxRate) : undefined,
            })));
          }
        }
      } catch { /* tolerate — leave a blank form */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId, preset.basePath]);

  const productOptions: SearchableSelectOption[] = useMemo(
    () => productRows.map((r) => ({
      value: String(r.id ?? r.productId ?? r.code ?? ''),
      label: String(r.name ?? r.code ?? `#${r.id}`),
    })).filter((o) => o.value),
    [productRows],
  );

  /* When a product is picked on a line, auto-fill price + unit + tax.
     If the SAME product is already on another line, just increment that
     line's qty instead of duplicating it, and drop the row being edited. */
  function pickLineProduct(lineId: string, productValue: string) {
    const row = productRows.find((r) => String(r.id ?? r.productId ?? r.code) === productValue);
    if (!row) { updateLine(lineId, { productId: null, itemName: '' }); return; }
    const pickedId = (row.id ?? row.productId) as number | string;

    setLines((ls) => {
      // Guarantee the list always ends with ONE blank row so the operator can
      // keep adding products without clicking "Add New" — and a 2nd pick never
      // overwrites the 1st (the previous logic only appended when the picked
      // row happened to be the last one, so merges / non-last picks lost the
      // trailing blank and items appeared to overwrite each other).
      const ensureBlankTail = (rows: typeof ls) => {
        const last = rows[rows.length - 1];
        const blank = last && last.productId == null && !String(last.itemName ?? '').trim();
        return blank ? rows : [...rows, newLine()];
      };
      const existingIdx = ls.findIndex(
        (l) => l.id !== lineId && l.productId != null && String(l.productId) === String(pickedId),
      );
      if (existingIdx >= 0) {
        let next = ls.map((l, i) =>
          i === existingIdx ? { ...l, qty: Number(l.qty || 0) + 1 } : l,
        );
        // Drop the line the user picked into (it would be a duplicate),
        // unless it's the only row — then blank it.
        if (next.length > 1) {
          next = next.filter((l) => l.id !== lineId);
        } else {
          next = next.map((l) => (l.id === lineId ? newLine() : l));
        }
        return ensureBlankTail(next);
      }
      // New product → fill the current line.
      const filled = ls.map((l) =>
        l.id === lineId
          ? {
              ...l,
              productId: pickedId,
              itemName: String(row.name ?? row.code ?? ''),
              price: Number(row.price ?? row.sellingPrice ?? row.mrp ?? 0),
              unit: String(row.unitOfMeasure ?? row.unit ?? ''),
              taxRate: Number(row.taxRate ?? 0) || 18,
            }
          : l,
      );
      return ensureBlankTail(filled);
    });
  }

  /** Selecting a party auto-fills its phone + pincode; setting the pincode
   *  then drives the Place-of-Supply lookup (usePincodeLookup above). */
  function pickParty(v: string) {
    setPartyId(v);
    const row = partyRows.find((r) => String(r.id ?? r[`${preset.partyResource}Id`] ?? r.code ?? '') === v);
    if (!row) return;
    const ph = String(row.phone ?? row.mobile ?? row.phoneNumber ?? row.contactNumber ?? '').replace(/\D/g, '').slice(0, 10);
    if (ph) setPhone(ph);
    const pc = String(row.postalCode ?? row.pincode ?? row.pinCode ?? row.zip ?? '').replace(/\D/g, '').slice(0, 6);
    if (pc) setPin(pc);
    // Billing address — prefer the customer's structured `billingAddress`
    // column; only compose from address parts when that field is empty.
    const composed = [
      row.address ?? row.addressLine1, row.addressLine2,
      row.city, row.state, (row.postalCode ?? row.pinCode ?? row.pincode),
    ].map((x) => String(x ?? '').trim()).filter(Boolean).join(', ');
    const bill = String(row.billingAddress ?? '').trim() || composed;
    // Ship-to — use the customer's own shippingAddress if it differs; else
    // mirror billing (and keep the "same as billing" toggle in sync).
    const custShip = String(row.shippingAddress ?? '').trim();
    if (bill) {
      setBillingAddress(bill);
      if (custShip && custShip !== bill) {
        setShippingAddress(custShip);
        setShipSameAsBill(false);
      } else if (shipSameAsBill) {
        setShippingAddress(bill);
      }
    }
  }

  /* ----- Quick-add party (customer / supplier) --------------- */
  const [qpOpen, setQpOpen] = useState(false);
  const [qpName, setQpName] = useState('');
  const [qpPhone, setQpPhone] = useState('');
  const [qpBusy, setQpBusy] = useState(false);
  async function quickAddParty() {
    if (!qpName.trim()) { toast.error('Name required'); return; }
    setQpBusy(true);
    try {
      const isCust = preset.partyResource === 'customer';
      const code = (isCust ? 'CUS-' : 'SUP-') + Date.now().toString(36).toUpperCase().slice(-6);
      const body: Record<string, unknown> = {
        code, name: qpName.trim(), description: '', status: 0, remarks: '',
        phone: qpPhone.trim(), email: '', gstin: '', pan: '',
        billingAddress: '', shippingAddress: '', city: '', state: '', pinCode: '',
        country: 'India', currency: 'INR',
      };
      if (isCust) { body.customerType = 'B2C'; body.creditLimit = 0; body.outstandingBalance = 0; body.loyaltyPoints = 0; }
      const r = await apiClient.post(partyUrl, body);
      const created = (r.data?.data ?? r.data) as Record<string, unknown>;
      const id = String(created.id ?? created.code ?? '');
      setPartyReload((k) => k + 1);
      setPartyId(id);
      setPhone(qpPhone.trim());
      setQpOpen(false); setQpName(''); setQpPhone('');
      toast.success(isCust ? 'Customer added' : 'Vendor added', qpName.trim());
    } catch (e) {
      toast.error('Quick add failed', e instanceof Error ? e.message : 'Try again');
    } finally { setQpBusy(false); }
  }

  /* ----- Quick-add product ----------------------------------- */
  const [qprodOpen, setQprodOpen] = useState(false);
  const [qprodLineId, setQprodLineId] = useState<string | null>(null);
  const [qprodName, setQprodName] = useState('');
  const [qprodPrice, setQprodPrice] = useState('');
  const [qprodBusy, setQprodBusy] = useState(false);
  async function quickAddProduct() {
    if (!qprodName.trim()) { toast.error('Product name required'); return; }
    setQprodBusy(true);
    try {
      const code = 'PRD-' + Date.now().toString(36).toUpperCase().slice(-6);
      const body = {
        code, name: qprodName.trim(), sku: code, description: '', status: 0, remarks: '',
        price: Number(qprodPrice) || 0, sellingPrice: Number(qprodPrice) || 0,
        mrp: Number(qprodPrice) || 0, taxRate: 18, currency: 'INR',
        unitOfMeasure: 'PCS', isService: false, isTrackStock: true,
      };
      const r = await apiClient.post('/master-data/Product', body);
      const created = (r.data?.data ?? r.data) as Record<string, unknown>;
      setProductReload((k) => k + 1);
      // assign to the line that triggered the add
      if (qprodLineId) {
        updateLine(qprodLineId, {
          productId: (created.id ?? created.productId) as number | string,
          itemName: qprodName.trim(),
          price: Number(qprodPrice) || 0,
          unit: 'PCS',
          taxRate: 18,
        });
      }
      setQprodOpen(false); setQprodName(''); setQprodPrice(''); setQprodLineId(null);
      toast.success('Product added', qprodName.trim());
    } catch (e) {
      toast.error('Quick add product failed', e instanceof Error ? e.message : 'Try again');
    } finally { setQprodBusy(false); }
  }

  /* ----- Totals ---------------------------------------------- */
  const totals = useMemo(() => {
    let subTotal = 0;
    let taxable = 0;
    let gst = 0;
    for (const l of lines) {
      const gross = (l.qty || 0) * (l.price || 0);
      subTotal += gross;
      taxable += gross;
      gst += gross * ((l.taxRate ?? 0) / 100);
    }
    return { subTotal, taxable, gst, grandTotal: subTotal + gst };
  }, [lines]);

  /* ----- Submit ---------------------------------------------- */
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
      const realLines = lines.filter((l) => l.itemName.trim() || l.productId != null);
      const partyName = partyOptions.find((o) => o.value === partyId)?.label ?? '';
      const isoDate = docDate ? new Date(docDate).toISOString() : new Date().toISOString();

      // Header payload — map form state to the REAL schema columns (probed
      // from live swagger) so customer / dates / totals / status land in the
      // actual fields the list grid + reports read. The `description` JSON is
      // kept too, but only as a round-trip envelope for fields the schema has
      // no column for (mode, placeOfSupply, variant, addOns, phone).
      const payload: Record<string, unknown> = {
        code: docNumber,
        name: `${preset.title} ${docNumber}`,
        currency: 'INR',
        // §35: numeric row-level enum (0=Active). The workflow stage lives in
        // the per-entity *Status string column, mapped below.
        status: 0,
        // Recoverable fallback. The header `description` column has a small,
        // undocumented size limit — overflowing it 500s the POST with "string
        // would be truncated". So keep the (small) form metadata but trim each
        // line to essentials and drop trailing lines until it fits under a
        // safe cap. The separate *Line table holds the full lines.
        description: (() => {
          const meta = {
            mode, docDate, partyId, phone, placeOfSupply,
            validTill, deliveryDate, origBillNo, origBillDate,
            buyerPoNumber, transporter, totals, pin,
            // Addresses live in real columns; keep only a short copy in the
            // envelope so they never crowd line items out of the 700-char cap.
            ...(preset.addressFields ? {
              billingAddress: billingAddress.slice(0, 120),
              shippingAddress: shippingAddress.slice(0, 120),
            } : {}),
          };
          const mk = (rows: typeof realLines) => JSON.stringify({
            ...meta,
            lines: rows.map((l) => ({
              productId: l.productId ?? undefined,
              name: String(l.itemName ?? '').slice(0, 48),
              quantity: l.qty, price: l.price, taxRate: l.taxRate,
            })),
          });
          let rows = realLines.slice();
          let json = mk(rows);
          while (json.length > 700 && rows.length > 0) { rows = rows.slice(0, -1); json = mk(rows); }
          return json;
        })(),
        ...(preset.numberField ? { [preset.numberField]: docNumber } : {}),
        ...(preset.dateField ? { [preset.dateField]: isoDate } : {}),
        // Link to the source document (e.g. SalesReturn.salesInvoiceId) so the
        // return is tied to its origin invoice, not just captured as free text.
        ...(refCfg?.headerFkField && refId ? { [refCfg.headerFkField]: Number(refId) } : {}),
        ...(preset.showOriginalBill && origBillNo ? { reason: origBillNo } : {}),
        ...(preset.partyIdField ? { [preset.partyIdField]: partyId ? Number(partyId) : null } : {}),
        ...(preset.partyNameField ? { [preset.partyNameField]: partyName } : {}),
        // Real billing/shipping columns (probed present on SalesOrder). Gated so
        // we never write them to docs whose schema lacks the columns.
        ...(preset.addressFields ? { billingAddress, shippingAddress } : {}),
        ...(preset.statusField ? { [preset.statusField]: preset.statusValue ?? 'Open' } : {}),
        // warehouseId — stored on docs that have the column (e.g. PurchaseOrder);
        // dropped harmlessly where absent (e.g. PurchaseInvoice).
        ...(preset.showWarehouse && warehouseId ? { warehouseId: Number(warehouseId) } : {}),
        [preset.subTotalField ?? 'subTotal']: round2(totals.subTotal),
        [preset.taxField ?? 'taxAmount']: round2(totals.gst),
        [preset.grandTotalField ?? 'grandTotal']: round2(totals.grandTotal),
        // Payment captured at billing time (PurchaseInvoice has these columns).
        ...(preset.showPayment ? {
          paidAmount: round2(Math.min(amountPaid, totals.grandTotal)),
          balance: round2(Math.max(0, totals.grandTotal - amountPaid)),
          paymentStatus: paymentStatusFor(amountPaid, totals.grandTotal),
        } : {}),
      };

      // SalesInvoice's deployed backend validates header SubTotal against its
      // line items ("Invoice does not balance: items=0 header=N") AT CREATE,
      // when no lines exist yet. For that one entity, create with ZERO totals
      // (0 balances 0), then reconcile the real totals after the lines post.
      // Other docs (SalesOrder/etc.) accept real totals on create, so leave
      // them untouched to avoid regressing a working path.
      const needsBalancedCreate = preset.basePath === '/sales/SalesInvoice';
      const zeroTotals = {
        [preset.subTotalField ?? 'subTotal']: 0,
        [preset.taxField ?? 'taxAmount']: 0,
        [preset.grandTotalField ?? 'grandTotal']: 0,
        ...(preset.showPayment ? { paidAmount: 0, balance: 0, paymentStatus: 'Unpaid' } : {}),
      };

      // Save header, capture its numeric id (needed as the line FK).
      let savedId: number | string | undefined;
      if (isEdit) {
        // The PUT endpoints resolve the key from the body's `id`, and many
        // rows (e.g. POS-created SalesInvoices) have an EMPTY `code` — so
        // sending only `code` made the backend look up id=0 → 404. Send the
        // captured numeric id when we have it.
        const idKey = editRowId != null && Number.isFinite(Number(editRowId))
          ? { id: Number(editRowId), code: docNumber }
          : { code: docNumber };
        const r = await apiClient.put(preset.basePath, { ...payload, ...idKey });
        const saved = (r.data?.data ?? r.data) as Record<string, unknown>;
        savedId = (saved?.id as number) ?? (editRowId != null ? Number(editRowId) : undefined);
      } else {
        // The create POST returns 200 with an EMPTY body (no id), so recover
        // the id by the unique `code` we just sent — otherwise the line-post
        // block below is skipped and the document saves with ZERO line rows.
        const { id } = await createHeaderAndGetId(preset.basePath, needsBalancedCreate ? { ...payload, ...zeroTotals } : payload, {
          matchField: 'code',
          matchValue: docNumber,
        });
        savedId = id;
      }

      // Lines → the REAL line table (when the entity exposes one). Without
      // this, items only lived in the header's description blob and never
      // appeared as SalesOrderLine / CreditNoteLine / … rows.
      const lbp = preset.lineBasePath;
      const lfk = preset.lineFkField;
      if (lbp && lfk && savedId != null) {
        if (isEdit) {
          // Clear prior lines so qty/price edits don't pile up duplicates.
          try {
            const ex = await apiClient.get(lbp);
            const exRows = (Array.isArray(ex.data) ? ex.data : (ex.data?.items ?? ex.data?.data ?? [])) as Array<Record<string, unknown>>;
            await Promise.all(
              exRows
                .filter((row) => String(row[lfk]) === String(savedId))
                .map((row) => apiClient.delete(`${lbp}/${row.id}`).catch(() => undefined)),
            );
          } catch { /* best-effort cleanup */ }
        }
        // Sequential so a parallel burst can't race the auth refresh and drop
        // lines (which left documents missing items).
        await postLinesSequential(lbp, realLines.map((l, i) => {
          const qty = Number(l.qty) || 0;
          const price = Number(l.price) || 0;
          const rate = Number(l.taxRate) || 0;
          const taxable = round2(qty * price);
          return {
            code: `${docNumber}-L${i + 1}`,
            name: l.itemName || `Line ${i + 1}`,
            status: 0,
            [lfk]: savedId,
            lineNumber: i + 1,
            productId: l.productId ?? null,
            productName: [l.itemName, l.variant].filter(Boolean).join(' — '),
            sku: '',
            quantity: qty,
            unitOfMeasure: l.unit || 'PCS',
            unitPrice: price,
            lineDiscount: 0,
            taxableAmount: taxable,           // present on some line schemas; dropped where absent
            taxRate: rate,
            taxAmount: round2(taxable * (rate / 100)),
            lineTotal: round2(taxable * (1 + rate / 100)),
            reason: origBillNo || undefined,  // SalesReturnLine.reason; ignored elsewhere
          };
        }));
      }

      // Reconcile real header totals now the lines exist (SalesInvoice only —
      // its create was sent with zero totals to pass the backend balance check).
      if (needsBalancedCreate && savedId != null) {
        const idKey = Number.isFinite(Number(savedId)) && Number(savedId) > 0
          ? { id: Number(savedId), code: docNumber }
          : { code: docNumber };
        try {
          await apiClient.put(preset.basePath, { ...payload, ...idKey });
        } catch { /* lines saved; totals will reconcile server-side */ }
      }

      // Purchase ↔ Inventory: a Purchase Invoice receives goods → stock IN.
      if (preset.stockDirection && savedId != null) {
        const n = await postStockMovements({
          direction: preset.stockDirection,
          sourceType: preset.basePath.split('/').pop() ?? preset.title.replace(/\s+/g, ''),
          sourceId: savedId,
          docNumber,
          occurredAt: isoDate,
          warehouseId: warehouseId ? Number(warehouseId) : null,
          lines: realLines.map((l) => ({
            productId: l.productId,
            quantity: Number(l.qty) || 0,
            unitCost: Number(l.price) || 0,
            unitOfMeasure: l.unit,
          })),
        });
        if (n > 0) toast.success('Stock updated', `${n} item(s) ${preset.stockDirection === 'In' ? 'added to' : 'removed from'} inventory`);
      }

      // Payments ↔ Billing: record what was paid to the supplier.
      if (preset.showPayment && amountPaid > 0 && savedId != null && partyId) {
        await postPurchasePayment({
          purchaseInvoiceId: savedId,
          supplierId: Number(partyId),
          amount: Math.min(amountPaid, totals.grandTotal),
          method: payMethod,
          docNumber,
        });
      }

      toast.success('Saved', `${preset.title} #${docNumber} ${isEdit ? 'updated' : 'created'}`);
      navigate(preset.listPath);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  }

  /* ----- Print / Share / WhatsApp / Email -------------------- */
  const r2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  async function buildPrintInput(): Promise<SalesOrderPrintInput> {
    // Inline uploaded logo/signature/QR (auth-only URLs) → data URIs for print.
    const eb = await getPrintBrandingInlined();
    const tenantH = await getTenantHeaderInlined();
    const real = lines.filter((l) => l.itemName.trim() || l.productId != null);
    let party: Record<string, unknown> = {};
    try {
      const url = preset.partyResource === 'customer' ? '/crm/Customer' : '/purchase/Supplier';
      const d = (await apiClient.get(url)).data;
      const rows = (Array.isArray(d) ? d : (d?.items ?? d?.data ?? [])) as Array<Record<string, unknown>>;
      party = rows.find((x) => String(x.id ?? '') === String(partyId)) ?? {};
    } catch { /* best effort — print with what we have */ }
    const partyName = partyOptions.find((o) => o.value === partyId)?.label ?? String(party.name ?? '');
    const billTo = {
      label: preset.partyResource === 'customer' ? 'Bill To (Receiver)' : 'Vendor',
      name: partyName,
      address: String(party.address ?? party.billingAddress ?? ''),
      city: party.city ? String(party.city) : undefined,
      state: party.state ? String(party.state) : (placeOfSupply || undefined),
      pincode: String(party.pincode ?? pin ?? ''),
      gstin: String(party.gstin ?? ''),
      phone: String(party.phone ?? party.mobile ?? phone ?? ''),
      email: String(party.email ?? ''),
    };
    // Resolve HSN per line from the product master (the line row has no HSN).
    const prodById: Record<string, Record<string, unknown>> = {};
    for (const p of productRows) prodById[String(p.id ?? p.productId)] = p;

    // Intra- vs inter-state from the GSTIN state codes (first 2 digits) —
    // drives the CGST/SGST vs IGST split instead of an arbitrary half.
    const custStateCode = String(billTo.gstin || '').slice(0, 2);
    const tenantStateCode = String(tenantH.gstin || '').slice(0, 2);
    const interstate = !!custStateCode && !!tenantStateCode && custStateCode !== tenantStateCode;

    // Prefer the saved header's real persisted amounts; fall back to the
    // live computed totals for an unsaved doc.
    const sv = savedRow ?? {};
    const subTotal = r2(Number(sv.subTotal ?? totals.subTotal) || 0);
    const taxAmount = r2(Number(sv.taxAmount ?? totals.gst) || 0);
    const discount = r2(Number(sv.discountAmount ?? 0) || 0);
    const transportCharges = r2(Number(sv.shippingCharge ?? 0) || 0);
    const totalAmount = r2(Number(sv.grandTotal ?? totals.grandTotal) || 0);
    const roundOff = r2(Number(sv.roundingAmount ?? sv.roundOff ?? (totalAmount - (subTotal - discount + taxAmount + transportCharges))) || 0);

    return {
      docTitle: preset.title.toUpperCase(),
      number: docNumber,
      date: String(sv[preset.dateField ?? 'orderDate'] ?? docDate) || new Date().toISOString(),
      buyerPoNumber: buyerPoNumber || undefined,
      salesPerson: sv.salesPersonName ? String(sv.salesPersonName) : undefined,
      paymentTerms: mode || undefined,
      tenant: tenantH,
      billTo,
      shipTo: { ...billTo, label: 'Ship To (Consignee)' },
      lines: real.map((l) => {
        const prod = prodById[String(l.productId ?? '')] ?? {};
        return {
          description: [l.itemName, l.variant].filter(Boolean).join(' — '),
          hsnCode: prod.hsnCode ? String(prod.hsnCode) : (prod.hsn ? String(prod.hsn) : undefined),
          qty: Number(l.qty) || 0,
          unit: l.unit,
          rate: Number(l.price) || 0,
          amount: r2((Number(l.qty) || 0) * (Number(l.price) || 0)),
        };
      }),
      subTotal,
      tradeDiscount: discount,
      transportCharges,
      cgstAmount: interstate ? 0 : r2(taxAmount / 2),
      sgstAmount: interstate ? 0 : r2(taxAmount / 2),
      igstAmount: interstate ? taxAmount : 0,
      taxableAmount: r2(Math.max(0, subTotal - discount)),
      roundOff,
      totalAmount,
      transporter: transporter || undefined,
      bank: bankInfo ?? ((eb.bankName || eb.bankAccountNo) ? {
        accountName: eb.bankAccountName, bankName: eb.bankName, accountNo: eb.bankAccountNo, ifsc: eb.bankIfsc, branch: eb.bankBranch,
      } : undefined),
      signatureUrl: eb.signatureUrl,
      qrUrl: printQrUrl(eb.qrUrl, [preset.title.toUpperCase(), docNumber, billTo.name, 'Amt:' + totalAmount, tenantH.gstin]),
    };
  }
  async function printDoc() { setActionsOpen(false); printSalesOrder(await buildPrintInput()); }
  function summaryText(): string {
    const partyName = partyOptions.find((o) => o.value === partyId)?.label ?? '';
    const n = lines.filter((l) => l.productId != null || l.itemName.trim()).length;
    return `${preset.title} ${docNumber}\n${partyName ? `Party: ${partyName}\n` : ''}Total: ₹ ${totals.grandTotal.toFixed(2)}\nItems: ${n}`;
  }
  async function doShare() {
    setActionsOpen(false);
    const text = summaryText();
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (typeof nav.share === 'function') await nav.share({ title: `${preset.title} ${docNumber}`, text });
      else { await navigator.clipboard.writeText(text); toast.success('Copied', 'Summary copied to clipboard'); }
    } catch { /* user dismissed the share sheet */ }
  }
  function doWhatsApp() {
    setActionsOpen(false);
    const ph = String(phone ?? '').replace(/\D/g, '').replace(/^0+/, '');
    const intl = ph.length === 10 ? `91${ph}` : ph;
    const msg = encodeURIComponent(summaryText() + '\n\nThank you!');
    window.open(`https://wa.me/${intl}?text=${msg}`, '_blank', 'noopener');
  }
  function doEmail() {
    setActionsOpen(false);
    const subject = encodeURIComponent(`${preset.title} ${docNumber}`);
    const body = encodeURIComponent(summaryText());
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  }

  /* ============================================================ */
  /*  Render                                                      */
  /* ============================================================ */
  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col bg-surface-app">
      {/* ─────── Header ─────── */}
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(preset.listPath)}
          className="grid h-9 w-9 place-items-center rounded-md border border-border hover:bg-surface-card-hover"
          aria-label="Back to list"
        >
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-lg font-semibold text-content-primary">
          {isEdit ? 'Edit' : 'New'} {preset.title}
        </h1>
        <div className="ml-auto flex items-center gap-2">
          {preset.modeToggle === 'credit-cash' && (
            <ToggleRow
              value={mode}
              options={[{ label: 'Credit', value: 'Credit' }, { label: 'Cash', value: 'Cash' }]}
              onChange={setMode}
            />
          )}
          {preset.modeToggle === 'with-without-item' && (
            <RadioRow
              value={mode}
              options={[{ label: 'With Item', value: 'WithItem' }, { label: 'Without Item', value: 'WithoutItem' }]}
              onChange={setMode}
            />
          )}
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-md border border-border text-primary hover:bg-surface-card-hover"
            title="Settings"
          >
            <Settings size={16} />
          </button>
        </div>
      </header>

      {/* ─────── Scrollable body ─────── */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

        {/* === Business Info card === */}
        <Card className="mb-4 p-4">
          <h2 className="mb-3 text-sm font-medium text-content-secondary">Business Info :</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <FloatField label={preset.docNumberLabel} value={docNumber} onChange={setDocNumber} />
            <FloatField
              label="Date"
              value={docDate}
              onChange={setDocDate}
              type="date"
              icon={<CalendarIcon size={14} />}
            />
          </div>
        </Card>

        {/* === Party Info card === */}
        <Card className="mb-4 p-4">
          <h2 className="mb-3 text-sm font-medium text-content-secondary">
            {preset.partyResource === 'customer' ? 'Customer Info' : 'Vendor Info'}
          </h2>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-content-secondary">{preset.partyLabel}</span>
                <button
                  type="button"
                  onClick={() => setQpOpen(true)}
                  className="text-[11px] font-medium text-primary hover:underline"
                >
                  + Quick add
                </button>
              </div>
              <SearchableSelect
                value={partyId}
                onChange={pickParty}
                options={partyOptions}
                placeholder={preset.partyResource === 'customer' ? 'Select customer' : 'Select vendor'}
                searchPlaceholder="Search…"
              />
            </div>
            <FloatField label="Phone Number" value={phone} onChange={(v) => setPhone(v.replace(/\D/g, ''))} maxLength={10} />
            <FloatField label="Place of Supply" value={placeOfSupply} onChange={setPlaceOfSupply} />
            <FloatField label="Pincode" value={pin} onChange={(v) => setPin(v.replace(/\D/g, ''))} maxLength={6} />
            {preset.partyResource === 'customer' && (
              <FloatField label="Buyer P.O. No" value={buyerPoNumber} onChange={setBuyerPoNumber} />
            )}
            <FloatField label="Transporter" value={transporter} onChange={setTransporter} />
            {preset.showWarehouse && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-content-secondary">
                  Warehouse{preset.stockDirection ? ' *' : ''}
                </span>
                <SearchableSelect
                  value={warehouseId}
                  onChange={setWarehouseId}
                  options={warehouseOptions}
                  placeholder="Select warehouse"
                  searchPlaceholder="Search…"
                />
              </div>
            )}
            {preset.showPayment && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-content-secondary">Amount Paid</span>
                <div className="flex gap-1">
                  <select
                    className="h-9 rounded border border-border bg-surface-card px-1 text-xs outline-none focus:border-primary"
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                    title="Payment method"
                  >
                    {['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    type="number"
                    className="h-9 w-full rounded border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                    value={amountPaid}
                    placeholder="0"
                    onChange={(e) => setAmountPaid(Number(e.target.value) || 0)}
                  />
                </div>
              </div>
            )}
            {preset.showDeliveryDates && (
              <>
                <FloatField label="Valid Till *" value={validTill} onChange={setValidTill} type="date" icon={<CalendarIcon size={14} />} />
                <FloatField label="Delivery Date *" value={deliveryDate} onChange={setDeliveryDate} type="date" icon={<CalendarIcon size={14} />} />
              </>
            )}
            {refCfg && (() => {
              // Noun derived from the reference doc's number field so the copy
              // reads "order"/"invoice" correctly (DeliveryChallan loads from a
              // Sales Order, SalesReturn from an Invoice).
              const refNoun = (refCfg.numberField || '').replace(/number$/i, '').trim() || 'document';
              return (
                <div className="flex flex-col gap-1 rounded-md border border-primary/30 bg-primary/5 p-2">
                  <span className="text-[11px] font-medium text-primary">{refCfg.label} *</span>
                  <SearchableSelect
                    value={refId}
                    onChange={(v) => void importFromReference(v)}
                    options={refOptions}
                    placeholder={`Select the ${refNoun} to load its items`}
                    searchPlaceholder={`Search ${refNoun}…`}
                  />
                  <span className="text-[10px] text-content-muted">
                    Pick the source {refNoun} — its customer and line items load automatically; then edit the quantities.
                  </span>
                </div>
              );
            })()}
            {preset.showOriginalBill && (
              <>
                <FloatField label="Org Bill Number" value={origBillNo} onChange={setOrigBillNo} />
                <FloatField label="Org Bill Date" value={origBillDate} onChange={setOrigBillDate} type="date" icon={<CalendarIcon size={14} />} />
              </>
            )}
          </div>

          {preset.addressFields && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-content-secondary">Billing Address</span>
                <textarea
                  rows={2}
                  value={billingAddress}
                  onChange={(e) => {
                    setBillingAddress(e.target.value);
                    if (shipSameAsBill) setShippingAddress(e.target.value);
                  }}
                  placeholder="Auto-fills from the selected customer; edit if needed"
                  className="w-full resize-y rounded-md border border-border bg-surface-card px-3 py-2 text-sm outline-none focus:border-primary"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-content-secondary">Shipping Address</span>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-content-secondary">
                    <input
                      type="checkbox"
                      checked={shipSameAsBill}
                      onChange={(e) => {
                        setShipSameAsBill(e.target.checked);
                        if (e.target.checked) setShippingAddress(billingAddress);
                      }}
                    />
                    Same as billing
                  </label>
                </div>
                <textarea
                  rows={2}
                  value={shippingAddress}
                  disabled={shipSameAsBill}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder={shipSameAsBill ? 'Mirrors billing address' : 'Ship-to address'}
                  className="w-full resize-y rounded-md border border-border bg-surface-card px-3 py-2 text-sm outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            </div>
          )}
        </Card>

        {/* === Line Item Stock card === */}
        <Card className="mb-4 p-0">
          <button
            type="button"
            onClick={() => setLinesOpen((v) => !v)}
            className="flex w-full items-center justify-between border-b border-border px-4 py-3 text-left"
          >
            <span className="text-sm font-medium text-content-primary">Line Item Stock</span>
            {linesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {linesOpen && (
            <div className="p-4">
              {/* Barcode + cog */}
              <div className="mb-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    placeholder="Barcode"
                    className="h-10 w-full rounded-md border border-border bg-surface-card pl-10 pr-3 text-sm outline-none focus:border-primary"
                  />
                  <ScanLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                </div>
                <button
                  type="button"
                  className="grid h-9 w-9 place-items-center rounded-md border border-border text-primary"
                  title="Line settings"
                >
                  <Settings size={16} />
                </button>
              </div>

              {/* Line items table */}
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-surface-card-hover text-content-secondary">
                    <tr>
                      <th className="w-12 py-2 text-center">Serial No.</th>
                      <th className="px-2 py-2 text-left">Item</th>
                      {preset.lineExtras && <th className="px-2 py-2 text-left">Variant</th>}
                      {preset.lineExtras && <th className="px-2 py-2 text-left">Add Ons</th>}
                      <th className="w-20 px-2 py-2 text-right">Qty</th>
                      <th className="w-20 px-2 py-2 text-left">Unit</th>
                      <th className="w-24 px-2 py-2 text-right">Price / Unit</th>
                      <th className="w-24 px-2 py-2 text-right">Gross Amt</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const gross = (l.qty || 0) * (l.price || 0);
                      return (
                        <tr key={l.id} className="border-t border-border">
                          <td className="py-1.5 text-center text-content-muted">{i + 1}</td>
                          <td className="px-1 py-1" style={{ minWidth: 200 }}>
                            <div className="flex items-center gap-1">
                              <div className="flex-1">
                                <SearchableSelect
                                  value={l.productId != null ? String(l.productId) : ''}
                                  onChange={(v) => pickLineProduct(l.id, v)}
                                  options={productOptions}
                                  placeholder="Select product"
                                  searchPlaceholder="Search product…"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => { setQprodLineId(l.id); setQprodOpen(true); }}
                                className="grid h-8 w-8 shrink-0 place-items-center rounded border border-border text-primary hover:bg-surface-card-hover"
                                title="Quick add product"
                              >
                                <Plus size={13} />
                              </button>
                            </div>
                          </td>
                          {preset.lineExtras && (<>
                          <td className="px-1 py-1">
                            {variantsFor(l.productId).length > 0 ? (
                              <select
                                value={l.variant ?? ''}
                                onChange={(e) => {
                                  const v = variants.find((x) => variantLabel(x) === e.target.value && String(x.productId) === String(l.productId));
                                  updateLine(l.id, v ? { variant: variantLabel(v), price: Number(v.price ?? l.price) } : { variant: '' });
                                }}
                                className="h-9 w-full rounded border border-border bg-surface-card px-2 text-[13px] outline-none focus:border-primary"
                              >
                                <option value="">Base</option>
                                {variantsFor(l.productId).map((v) => (
                                  <option key={String(v.id)} value={variantLabel(v)}>
                                    {variantLabel(v)}{v.price != null ? ` · ₹${v.price}` : ''}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                value={l.variant ?? ''}
                                onChange={(e) => updateLine(l.id, { variant: e.target.value })}
                                placeholder="N/A"
                                className="h-9 w-full rounded border border-border bg-surface-card px-2 text-[13px] outline-none focus:border-primary"
                              />
                            )}
                          </td>
                          <td className="px-1 py-1">
                            <input
                              value={l.addOns ?? ''}
                              onChange={(e) => updateLine(l.id, { addOns: e.target.value })}
                              placeholder="—"
                              className="h-9 w-full rounded border border-border bg-surface-card px-2 text-[13px] outline-none focus:border-primary"
                            />
                          </td>
                          </>)}
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              value={l.qty}
                              onChange={(e) => updateLine(l.id, { qty: Number(e.target.value) || 0 })}
                              className="h-9 w-full rounded border border-border bg-surface-card px-2 text-right text-[13px] outline-none focus:border-primary"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              value={l.unit ?? ''}
                              onChange={(e) => updateLine(l.id, { unit: e.target.value })}
                              placeholder="Unit Type"
                              className="h-9 w-full rounded border border-border bg-surface-card px-2 text-[13px] outline-none focus:border-primary"
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input
                              type="number"
                              value={l.price}
                              onChange={(e) => updateLine(l.id, { price: Number(e.target.value) || 0 })}
                              className="h-9 w-full rounded border border-border bg-surface-card px-2 text-right text-[13px] outline-none focus:border-primary"
                            />
                          </td>
                          <td className="px-2 py-2 text-right text-content-primary">₹{gross.toFixed(2)}</td>
                          <td className="px-1 py-1 text-center">
                            <button
                              type="button"
                              onClick={() => removeLine(l.id)}
                              className="text-content-muted hover:text-danger"
                              title="Remove line"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {/* + Add New row */}
                    <tr className="border-t border-border bg-surface-card-hover/40">
                      <td colSpan={9} className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={addLine}
                            className="inline-flex items-center gap-1 text-[13px] font-medium text-primary"
                          >
                            <Plus size={13} /> Add New
                          </button>
                          {/* #2 — pick a bundle to append its component products. */}
                          {bundleOptions.length > 0 && (
                            <div className="w-56">
                              <SearchableSelect
                                value=""
                                onChange={(v) => v && addBundle(v)}
                                options={bundleOptions}
                                placeholder="+ Add bundle"
                                searchPlaceholder="Search bundle…"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

        {/* === Summary card === */}
        <Card className="p-4">
          <h2 className="mb-3 text-sm font-medium text-content-secondary">Summary</h2>
          <div className="grid gap-2 text-[13px] sm:grid-cols-2 lg:grid-cols-4">
            <KV label="Sub Total"   value={`₹ ${totals.subTotal.toFixed(2)}`} />
            <KV label="Taxable"     value={`₹ ${totals.taxable.toFixed(2)}`} />
            <KV label="GST"         value={`₹ ${totals.gst.toFixed(2)}`} />
            <KV label="Grand Total" value={`₹ ${totals.grandTotal.toFixed(2)}`} highlight />
          </div>
        </Card>
      </div>

      {/* ─────── Footer action bar (sticky) ─────── */}
      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-surface-card px-4 py-3">
        <div className="text-sm text-content-muted">
          Grand total <span className="ml-2 text-base font-semibold text-content-primary">₹ {totals.grandTotal.toFixed(2)}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(preset.listPath)} disabled={saving}>
            Cancel
          </Button>
          {/* Print / Share / WhatsApp / Email */}
          <div className="relative">
            <Button
              variant="outline"
              disabled={lines.filter((l) => l.productId != null || l.itemName.trim()).length === 0}
              onClick={() => setActionsOpen((v) => !v)}
              title="Print, share, or send this document"
            >
              <Share2 className="h-4 w-4" /> Print / Share
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${actionsOpen ? 'rotate-180' : ''}`} />
            </Button>
            {actionsOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setActionsOpen(false)} />
                <div className="absolute bottom-full right-0 z-20 mb-1 w-48 overflow-hidden rounded-md border border-border bg-surface-card py-1 shadow-lg">
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted" onClick={() => void printDoc()}>
                    <Printer className="h-4 w-4 text-content-muted" /> Print / Save PDF
                  </button>
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted" onClick={() => void doShare()}>
                    <Share2 className="h-4 w-4 text-content-muted" /> Share
                  </button>
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted" onClick={doWhatsApp}>
                    <MessageCircle className="h-4 w-4 text-content-muted" /> WhatsApp
                  </button>
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted" onClick={doEmail}>
                    <Mail className="h-4 w-4 text-content-muted" /> Email
                  </button>
                </div>
              </>
            )}
          </div>
          <Button onClick={save} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </footer>

      {/* ─────── Quick-add party modal ─────── */}
      {qpOpen && (
        <QuickAddModal
          title={preset.partyResource === 'customer' ? 'Quick add customer' : 'Quick add vendor'}
          busy={qpBusy}
          onClose={() => setQpOpen(false)}
          onSave={quickAddParty}
        >
          <QuickField label="Name *" value={qpName} onChange={setQpName} autoFocus />
          <QuickField label="Mobile number" value={qpPhone} onChange={(v) => setQpPhone(v.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" />
        </QuickAddModal>
      )}

      {/* ─────── Quick-add product modal ─────── */}
      {qprodOpen && (
        <QuickAddModal
          title="Quick add product"
          busy={qprodBusy}
          onClose={() => setQprodOpen(false)}
          onSave={quickAddProduct}
        >
          <QuickField label="Product name *" value={qprodName} onChange={setQprodName} autoFocus />
          <QuickField label="Price (₹)" value={qprodPrice} onChange={(v) => setQprodPrice(v.replace(/[^\d.]/g, ''))} placeholder="0.00" />
        </QuickAddModal>
      )}
    </div>
  );
}

/* ----- Quick-add modal shell + field ------------------------- */
function QuickAddModal({
  title, busy, onClose, onSave, children,
}: { title: string; busy: boolean; onClose: () => void; onSave: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-sm rounded-lg bg-surface-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">{title}</h3>
          <button type="button" onClick={onClose} disabled={busy} className="rounded p-1 text-content-muted hover:bg-surface-card-hover">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3">
          {children}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={busy}>{busy ? 'Adding…' : 'Add + select'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickField({
  label, value, onChange, placeholder, autoFocus,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-content-secondary">{label}</span>
      <input
        className="h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary"
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/* ============================================================ */
/*  Sub-components                                              */
/* ============================================================ */

function FloatField({
  label, value, onChange, type = 'text', maxLength, icon,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  maxLength?: number;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-content-secondary">{label}</span>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          className={
            'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary ' +
            (icon ? 'pr-9' : '')
          }
        />
        {icon && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-content-muted">{icon}</span>}
      </div>
    </div>
  );
}

function ToggleRow({
  value, options, onChange,
}: { value: string; options: { label: string; value: string }[]; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex items-center rounded-md border border-border bg-surface-card p-0.5 text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded px-3 py-1.5 ${
            value === o.value ? 'bg-primary text-white' : 'text-content-secondary hover:text-content-primary'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RadioRow({
  value, options, onChange,
}: { value: string; options: { label: string; value: string }[]; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-4 text-xs text-content-secondary">
      {options.map((o) => (
        <label key={o.value} className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="radio"
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="accent-primary"
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function KV({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between rounded-md border border-border px-3 py-2 ${highlight ? 'bg-primary/5' : 'bg-surface-card'}`}>
      <span className="text-content-muted">{label}</span>
      <span className={`font-semibold ${highlight ? 'text-primary' : 'text-content-primary'}`}>{value}</span>
    </div>
  );
}

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/* ============================================================ */
/*  Preset registry                                             */
/* ============================================================ */

/**
 * Flat (non-nested) URL prefix for each transaction kind.
 *
 * - List page:  `/panel/<slug>`           (e.g. /panel/credit-notes)
 * - Create:     `/panel/<slug>/new`       (e.g. /panel/credit-notes/new)
 * - Edit:       `/panel/<slug>/:id`       (e.g. /panel/credit-notes/123)
 *
 * Replaces the older `/panel/api/sales/CreditNote/...` nested path —
 * cleaner URLs, breadcrumbs read like "Panel > Credit Notes" instead
 * of "Panel > Api > Sales > CreditNote".
 */
export const KIND_SLUG: Record<string, string> = {
  SalesOrder:      'sales-orders',
  SalesInvoice:    'sales-invoices',
  SalesReturn:     'sales-returns',
  CreditNote:      'credit-notes',
  DebitNote:       'debit-notes',
  DeliveryChallan: 'delivery-challans',
  PurchaseOrder:   'purchase-orders',
  PurchaseInvoice: 'purchase-invoices',
};

export const PRESETS: Record<string, TransactionKindPreset> = {
  SalesOrder: {
    title: 'Sales Order', docNumberLabel: 'Sales Order No', partyLabel: 'Customer*',
    partyResource: 'customer', basePath: '/sales/SalesOrder',
    listPath: '/panel/sales-orders',
    modeToggle: 'credit-cash', showDeliveryDates: true,
    numberField: 'orderNumber', dateField: 'orderDate',
    partyIdField: 'customerId', partyNameField: 'customerName',
    statusField: 'orderStatus', statusValue: 'Open',
    lineBasePath: '/sales/SalesOrderLine', lineFkField: 'salesOrderId',
    addressFields: true, // live SalesOrder has billingAddress + shippingAddress columns
  },
  SalesInvoice: {
    title: 'Sales Invoice', docNumberLabel: 'Invoice No', partyLabel: 'Customer*',
    partyResource: 'customer', basePath: '/sales/SalesInvoice',
    listPath: '/panel/sales-invoices',
    modeToggle: 'credit-cash',
    numberField: 'invoiceNumber', dateField: 'invoiceDate',
    partyIdField: 'customerId', partyNameField: 'customerName',
    statusField: 'invoiceStatus', statusValue: 'Draft',
    lineBasePath: '/sales/SalesInvoiceLine', lineFkField: 'salesInvoiceId',
  },
  SalesReturn: {
    title: 'Sales Return', docNumberLabel: 'Return No', partyLabel: 'Customer*',
    partyResource: 'customer', basePath: '/sales/SalesReturn',
    listPath: '/panel/sales-returns',
    modeToggle: 'with-without-item', showOriginalBill: true,
    numberField: 'returnNumber', dateField: 'returnDate',
    partyIdField: 'customerId', partyNameField: 'customerName',
    statusField: 'refundStatus', statusValue: 'Pending',
    grandTotalField: 'refundAmount',
    lineBasePath: '/sales/SalesReturnLine', lineFkField: 'salesReturnId',
    showWarehouse: true, stockDirection: 'In', // returned goods come back into stock
    referenceResource: {
      basePath: '/sales/SalesInvoice', lineBasePath: '/sales/SalesInvoiceLine',
      lineFkField: 'salesInvoiceId', numberField: 'invoiceNumber', dateField: 'invoiceDate',
      label: 'Returning against invoice', headerFkField: 'salesInvoiceId',
    },
  },
  CreditNote: {
    title: 'Credit Note', docNumberLabel: 'Credit Note No', partyLabel: 'Customer*',
    partyResource: 'customer', basePath: '/sales/CreditNote',
    listPath: '/panel/credit-notes',
    modeToggle: 'with-without-item', showOriginalBill: true,
    numberField: 'creditNoteNumber', dateField: 'issueDate',
    partyIdField: 'customerId', partyNameField: 'customerName',
    lineBasePath: '/sales/CreditNoteLine', lineFkField: 'creditNoteId',
    stockDirection: 'In', // customer return → goods back (default warehouse; no picker on a credit note)
  },
  DebitNote: {
    title: 'Debit Note', docNumberLabel: 'Debit Note No', partyLabel: 'Vendor*',
    partyResource: 'supplier', basePath: '/sales/DebitNote',
    listPath: '/panel/debit-notes',
    modeToggle: 'with-without-item', showOriginalBill: true,
    // DebitNote now exposes supplierId/supplierName (verified live), so the
    // vendor is stored on the correct columns.
    numberField: 'debitNoteNumber', dateField: 'issueDate',
    partyIdField: 'supplierId', partyNameField: 'supplierName',
    subTotalField: 'amount',
    lineBasePath: '/sales/DebitNoteLine', lineFkField: 'debitNoteId',
    stockDirection: 'Out', // goods returned to supplier → stock out (default warehouse; no picker)
  },
  DeliveryChallan: {
    title: 'Delivery Challan', docNumberLabel: 'Challan No', partyLabel: 'Customer*',
    partyResource: 'customer', basePath: '/sales/DeliveryChallan',
    listPath: '/panel/delivery-challans',
    numberField: 'challanNumber', dateField: 'challanDate',
    partyIdField: 'customerId', partyNameField: 'customerName',
    statusField: 'challanStatus', statusValue: 'Pending',
    showWarehouse: true, // DeliveryChallan has a warehouseId column
    // No DeliveryChallanLine table exists on the backend — lines stay in
    // description JSON only until that endpoint ships (see BACKEND_GAPS).
    // Import the goods being delivered from a Sales Order ("Sale data"). The
    // header FK column is `relatedOrderId` (verified live), NOT salesOrderId.
    referenceResource: {
      basePath: '/sales/SalesOrder', lineBasePath: '/sales/SalesOrderLine',
      lineFkField: 'salesOrderId', numberField: 'orderNumber', dateField: 'orderDate',
      label: 'Deliver against Sales Order', headerFkField: 'relatedOrderId',
    },
  },
  PurchaseOrder: {
    title: 'Purchase Order', docNumberLabel: 'Purchase Order No', partyLabel: 'Vendor*',
    partyResource: 'supplier', basePath: '/purchase/PurchaseOrder',
    listPath: '/panel/purchase-orders',
    modeToggle: 'credit-cash', showDeliveryDates: true,
    numberField: 'orderNumber', dateField: 'orderDate',
    partyIdField: 'supplierId', // PurchaseOrder schema has no supplierName column
    statusField: 'orderStatus', statusValue: 'Open',
    lineBasePath: '/purchase/PurchaseOrderLine', lineFkField: 'purchaseOrderId',
    showWarehouse: true, // PO has a warehouseId column; a PO is non-stock (no stockDirection)
  },
  PurchaseInvoice: {
    title: 'Purchase Invoice', docNumberLabel: 'Invoice No', partyLabel: 'Vendor*',
    partyResource: 'supplier', basePath: '/purchase/PurchaseInvoice',
    listPath: '/panel/purchase-invoices',
    modeToggle: 'credit-cash',
    numberField: 'invoiceNumber', dateField: 'invoiceDate',
    partyIdField: 'supplierId', partyNameField: 'supplierName',
    statusField: 'invoiceStatus', statusValue: 'Draft',
    lineBasePath: '/purchase/PurchaseInvoiceLine', lineFkField: 'purchaseInvoiceId',
    // Billing a purchase = goods received → stock IN.
    showWarehouse: true, stockDirection: 'In', showPayment: true,
  },
};

export type TransactionKind = string;
